import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './FinalReview.css';
import logo from "../../assets/fishtagged-logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faGift, faFish, faAngleRight, faAngleLeft, faXmark, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import Hamburger from '../components/Hamburger';
import { loadImages, clearImages } from '../../imageDB';
import { useFileContext } from '../../FileContext';
import { snapshotCurrentReport, upsertReport, markReportUploadStatus } from '../../reportStorage';

const GiftIcon = () => <FontAwesomeIcon icon={faGift} size="1.5em" color="currentColor" />;
const FishSpeciesIcon = () => <FontAwesomeIcon icon={faFish} size="1.5em" color="currentColor" />;

function getFtUserData() {
  const match = document.cookie.match(/(^|; )ft_user_data=([^;]+)/);
  if (!match) return null;
  try {
    const decoded = atob(match[2]);
    const parsed = JSON.parse(decoded);
    if (parsed.roles && typeof parsed.roles === 'string') parsed.roles = JSON.parse(parsed.roles);
    return parsed;
  } catch (err) {
    console.error('Failed to parse ft_user_data cookie:', err);
    return null;
  }
}

async function findInvalidStep(reportData, fishData, anonymityData) {
  if (!anonymityData?.observationType) return "/anonymity";
  const images = await loadImages();
  if (!images || images.length === 0) return "/uploadFiles";
  if (!reportData) return "/reportImage";
  const locOk = reportData.noLocation
    ? !!reportData.landmark?.trim()
    : !!(reportData.latitude && reportData.longitude);
  if (!reportData.dateTaken || !reportData.timeTaken || !locOk) return "/reportImage";
  if (!fishData) return "/fishInfo";
  const { species, lenWhole, lenUnit, tagColor, tagPrefix, tagNumber } = fishData;
  if (!species || !lenWhole || !lenUnit || !tagColor || !tagPrefix || !tagNumber) return "/fishInfo";
  return null;
}

const STEP_LABELS = {
  "/anonymity":   "Anonymity (Step 1)",
  "/uploadFiles": "Upload Images (Step 2)",
  "/reportImage": "Report Image (Step 3)",
  "/fishInfo":    "Fish Information (Step 4)",
};

export default function FinalReviewScreen({ user }) {
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const { getRawFile } = useFileContext();

  const [optOutRewards, setOptOutRewards] = useState(() => sessionStorage.getItem("optOutRewards") === "true");
  const [reportData, setReportData]       = useState(null);
  const [fishData, setFishData]           = useState(null);
  const [anonymityData, setAnonymityData] = useState(null);
  const [sessionInfo, setSessionInfo]     = useState(null);
  const [submitting, setSubmitting]       = useState(false);

  const [incompleteStep, setIncompleteStep]           = useState(null);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);

  useEffect(() => {
    const rd = sessionStorage.getItem("reportImageData");
    const fd = sessionStorage.getItem("fishInfoData");
    const ad = sessionStorage.getItem("anonymityData");
    if (rd) try { setReportData(JSON.parse(rd)); } catch {}
    if (fd) try { setFishData(JSON.parse(fd)); } catch {}
    if (ad) try { setAnonymityData(JSON.parse(ad)); } catch {}
  }, [currentLocation]);

  useEffect(() => {
    sessionStorage.setItem("optOutRewards", optOutRewards);
  }, [optOutRewards]);

  useEffect(() => {
    setSessionInfo(getFtUserData());
  }, []);

  const isIneligibleForRewards = () => {
    if (anonymityData?.isAnonymous) return true;
    if (!sessionInfo) return true;
    return sessionInfo?.roles?.affiliation?.fishtagged === "GRANT";
  };

  const getRewardsStatus = () => {
    if (anonymityData?.isAnonymous) return "This entry is INELIGIBLE because it is submitted anonymously.";
    if (!sessionInfo) return "This entry is INELIGIBLE for a chance at a reward because you are not logged in.";
    if (optOutRewards) return "You have OPTED OUT of participating in the FishTagged rewards program.";
    if (sessionInfo?.roles?.affiliation?.fishtagged === "GRANT") return "This entry is INELIGIBLE for a chance at a reward because your profile indicates you work at FishTagged.";
    return "This entry MAY BE ELIGIBLE for a chance at a reward.";
  };

  const effectivelyOptedOut = optOutRewards || isIneligibleForRewards();

  const formatDate = v => {
    if (!v) return "—";
    const [y, m, d] = v.split("-");
    return y && m && d ? new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString() : v;
  };
  const formatTime = v => {
    if (!v) return "—";
    const [h, min] = v.split(":");
    if (!h || !min) return v;
    const d = new Date(); d.setHours(Number(h), Number(min));
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  const [observationType, setObservationType] = useState(null);

  useEffect(() => {
    const obs = sessionStorage.getItem("observationType");
    if (obs) setObservationType(obs);
  }, [currentLocation]);

  const dateTime = reportData ? `${formatDate(reportData.dateTaken)} at ${formatTime(reportData.timeTaken)}` : "—";
  const locationDisplay = () => {
    if (!reportData) return "—";
    if (reportData.noLocation) return reportData.landmark?.trim() ? `Landmark: ${reportData.landmark.trim()}` : "Not shared";
    const coords = reportData.latitude && reportData.longitude ? `${reportData.latitude}, ${reportData.longitude}` : null;
    if (coords && reportData.landmark) return `${coords} (${reportData.landmark})`;
    if (coords) return coords;
    if (reportData.landmark) return reportData.landmark;
    return "—";
  };
  const tagId = fishData ? `${fishData.tagPrefix}${fishData.tagNumber}` : "—";
  const length = fishData ? `${fishData.lenWhole}${fishData.lenFrac && fishData.lenFrac !== "0" ? ` ${fishData.lenFrac}` : ""} ${fishData.lenUnit}` : "—";

  const handleSubmit = async () => {
    const badStep = await findInvalidStep(reportData, fishData, anonymityData);
    if (badStep) {
      setIncompleteStep(badStep);
      setShowIncompleteModal(true);
      return;
    }

    setSubmitting(true);

    const activeId = sessionStorage.getItem("activeReportId") || undefined;
    const images = await loadImages();
    const savedReport = snapshotCurrentReport(activeId, images || []);
    upsertReport(savedReport);
    if (!sessionStorage.getItem("activeReportId")) {
      sessionStorage.setItem("activeReportId", savedReport.id);
    }
    const reportId = savedReport.id;

    try {
      const imageRecord = images.find(img => img.id === reportData?.selectedImageId) || images[0];
      const formData = new FormData();
      if (imageRecord) {
        const rawFile = getRawFile(imageRecord.id);
        if (rawFile) formData.append('image_0', rawFile, imageRecord.name || 'photo.jpg');
        else if (imageRecord.preview) {
          const res = await fetch(imageRecord.preview);
          formData.append('image_0', await res.blob(), imageRecord.name || 'photo.jpg');
        }
      }

      const dateStr = reportData?.dateTaken || '';
      const timeStr = reportData?.timeTaken || '';
      const offset = (() => {
        const o = new Date().getTimezoneOffset();
        const abs = Math.abs(o);
        const h = String(Math.floor(abs / 60)).padStart(2, '0');
        const m = String(abs % 60).padStart(2, '0');
        return (o <= 0 ? '+' : '-') + h + ':' + m;
      })();
      const record_datetime = dateStr && timeStr ? `${dateStr}T${timeStr}:00${offset}` : null;

      const lenWhole = parseFloat(fishData?.lenWhole) || 0;
      // eslint-disable-next-line
      const lenFrac = fishData?.lenFrac?.includes('/') ? eval(fishData.lenFrac) : parseFloat(fishData?.lenFrac) || 0;
      const fish_inches = fishData?.lenUnit === 'cm' ? (lenWhole + lenFrac) / 2.54 : lenWhole + lenFrac;

      const speciesMap = {
        'Smallmouth Bass': 'SMALLMOUTH_BASS', 'Largemouth Bass': 'LARGEMOUTH_BASS',
        'Walleye': 'WALLEYE', 'Channel Catfish': 'CATFISH', 'Carp': 'CARP',
      };
      const colorMap = {
        'Orange': 'ORANGE', 'Yellow': 'YELLOW', 'Red': 'RED',
        'Yellow-Green': 'YELLOW_GREEN', 'Green': 'GREEN',
        'White': 'WHITE', 'Black': 'BLACK', 'Purple': 'PURPLE',
      };

      const payload = {
        record_submit_anonymously: anonymityData?.isAnonymous ?? true,
        record_type: sessionStorage.getItem("recordType") || "RECAPTURE",
        record_location_gps_lat: reportData?.latitude ? parseFloat(reportData.latitude) : null,
        record_location_gps_long: reportData?.longitude ? parseFloat(reportData.longitude) : null,
        record_location_verbal_description: reportData?.landmark || null,
        record_tag_color: colorMap[fishData?.tagColor] || fishData?.tagColor?.toUpperCase() || 'NONE',
        record_tag_id: (fishData?.tagPrefix || '') + (fishData?.tagNumber || ''),
        record_tag_waterway: 'POTOMAC_RIVER',
        record_datetime,
        fish_inches: parseFloat(fish_inches.toFixed(2)),
        fish_species: speciesMap[fishData?.species] || 'TEST',
        fish_remarks: fishData?.remarks || null,
        rewards_opt_in: !effectivelyOptedOut,
      };
      formData.append('payload', JSON.stringify(payload));

      const status = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', 'https://records-mgmt-api.fishtagged.com/tagged_fish_sighting/create', true);
        xhr.withCredentials = true;
        xhr.onload = () => resolve(xhr.status);
        xhr.onerror = () => reject(new Error('offline'));
        xhr.send(formData);
      });

      if (status >= 200 && status < 300) {
        sessionStorage.removeItem('reportImageData');
        sessionStorage.removeItem('fishInfoData');
        sessionStorage.removeItem('anonymityData');
        sessionStorage.removeItem('activeReportId');
        clearImages(); 
        upsertReport({ ...savedReport, submitted: true, uploadStatus: null });
        navigate('/', { state: { showPopup: true } });

      } else if (status === 401 || status === 403) {
        markReportUploadStatus(reportId, 'reloginRequired');
        navigate('/', { state: { uploadNotice: 'reloginRequired' } });

      } else {
        markReportUploadStatus(reportId, 'failed');
        navigate('/', { state: { uploadNotice: 'failed' } });
      }

    } catch (err) {
      markReportUploadStatus(reportId, 'failed');
      navigate('/', { state: { uploadNotice: 'offline' } });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-image" />
      <header className="top-row">
        <div className="logo"><img src={logo} alt="FishTagged Logo" /></div>
        <div className="top-hamburger"><Hamburger user={user} /></div>
      </header>

      <div className="fr-viewport">
        <div className="fr-content">
          <div className="card-wrapper">
            <main className="fr-card">
              <p className="fr-step-label">New entry (5 out of 5)</p>
              <h1 className="fr-title">Final Review</h1>
              <p className="fr-subtitle">Please review the information below.</p>

              <div className="fr-form-box">
                <div className="fr-field-center">
                  <span className="fr-field-icon"><FishSpeciesIcon /></span>
                  <span className="fr-field-label">Observation Summary</span>
                </div>
                <div className="fr-horizontal-line"></div>
                <div className="two-columns">
                  <div className="fr-field-label-column">
                    <span>Observation Type</span>
                    <span>Date and Time</span>
                    <span>Location</span>
                    <span>Tag ID</span>
                    <span>Species</span>
                    <span>Length</span>
                    {fishData?.remarks && <span>Remarks</span>}
                  </div>
                  <div className="fr-field-value-column">
                    <span>{observationType || "—"}</span>
                    <span>{dateTime}</span>
                    <span>{locationDisplay()}</span>
                    <span>{tagId}</span>
                    <span>{fishData?.species || "—"}</span>
                    <span>{length}</span>
                    {fishData?.remarks && <span>{fishData.remarks}</span>}
                  </div>
                </div>
              </div>

              <div className="fr-section">
                <div className="fr-form-box">
                  <div className="fr-field-center">
                    <span className="fr-field-icon"><GiftIcon /></span>
                    <span className="fr-field-label">Rewards Eligibility</span>
                  </div>
                  <div className="fr-horizontal-line"></div>
                  <p className="fr-rewards-subtext">If you do not wish to participate in the rewards program:</p>
                  <div className="fr-rewards-opt">
                    <input
                      type="radio" id="opt-out-rewards" name="rewards"
                      checked={optOutRewards || isIneligibleForRewards()}
                      disabled={isIneligibleForRewards()}
                      onClick={() => { if (!isIneligibleForRewards()) setOptOutRewards(prev => !prev); }}
                      readOnly
                    />
                    <label htmlFor="opt-out-rewards">Opt out of rewards participation</label>
                  </div>
                  <div className="fr-reward-status">
                    <FontAwesomeIcon
                      icon={isIneligibleForRewards() || optOutRewards ? faXmark : faCheck}
                      className={isIneligibleForRewards() || optOutRewards ? "status-x" : "status-check"}
                    />
                    <span>{getRewardsStatus()}</span>
                  </div>
                </div>
              </div>
            </main>

            <div className="bottom-nav">
              <FontAwesomeIcon icon={faAngleLeft} className="arrow" onClick={() => navigate("/fishInfo")} />
              <button className="fi-submit-pill" onClick={handleSubmit} disabled={submitting} aria-label="Submit">
                <span className="fi-submit-text">{submitting ? 'Submitting…' : 'Submit'}</span>
                {!submitting && <FontAwesomeIcon icon={faAngleRight} className="submit-arrow" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showIncompleteModal && (
        <div className="ri-modal-overlay">
          <div className="ri-modal">
            <button className="ri-modal-close" onClick={() => setShowIncompleteModal(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <FontAwesomeIcon icon={faExclamationTriangle} className="ri-warning-icon" />
            <h2 className="ri-modal-title">Incomplete Entry</h2>
            <p className="ri-modal-body">
              <strong>{STEP_LABELS[incompleteStep]}</strong> is incomplete or has been modified. Please go back and complete it before submitting.
            </p>
            <div className="ri-modal-actions">
              <button className="ri-modal-cancel" onClick={() => setShowIncompleteModal(false)}>Cancel</button>
              <button className="ri-modal-choose" onClick={() => { setShowIncompleteModal(false); navigate(incompleteStep); }}>Go Fix It</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
